import { useCallback, useState, useContext, useRef, useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import {
  editDiscardButtonClicked,
  editKeepButtonClicked,
} from 'src/app/analytics/edit';
import { UTILS_RESET_PERSISTENT_DATA } from 'src/app/constants/dispatcherId';
import { ROUTE_EDIT_PAY_AND_POST } from 'src/app/constants/routes';
import getTrackingAdapter from 'src/app/dataAnalytics';
import {
  DISCARD_CHANGES_PRESSED,
  KEEP_EDITING_PRESSED,
} from 'src/app/dataAnalytics/schema/constants';
import {
  FEATURE_DATA_ANALYTICS,
  useFeatureToggleData,
} from 'src/app/featureToggle';
import { utils } from 'src/app/state/dispatcher';
import { actionUpdateIsDiscarding } from 'src/app/state/modules/app/isDiscarding';
import { sendLinkEvent } from 'src/app/utils/analytics';
import { EditFlowSectionsManagerContext } from 'src/app/views/context/EditFlowSectionsManagerContext';
import { useLocalisedRoutes } from 'src/app/views/hooks/useLocalisedRoutes';
import { useHeaderFooterLinkEventListener } from 'src/app/views/hooks/useNavLinkEventListener';
import type AppState from 'src/types/appState';
import type { TrackingEvent } from 'src/types/trackingData';

const DEFAULT_LINK_HREF = '';

export const useDiscardDialog = () => {
  const { tealium } = useSelector(
    (state: AppState) => ({
      tealium: state.tealium,
    }),
    shallowEqual,
  );

  const location = useLocation();
  const pathname = useRef(location.pathname);
  const isPageAnalyticsEnabled = useFeatureToggleData(FEATURE_DATA_ANALYTICS);

  useEffect(() => {
    pathname.current = location.pathname;
  }, [location.pathname]);

  const { sectionsManager: editPageSectionsManager } = useContext(
    EditFlowSectionsManagerContext,
  );

  const [isOpen, setIsOpen] = useState(false);
  const [linkHref, setLinkHref] = useState(DEFAULT_LINK_HREF);

  const dispatch = useDispatch();

  const { redirectToJobManagement } = useLocalisedRoutes();

  const resetLinkHref = () => {
    setLinkHref(DEFAULT_LINK_HREF);
  };

  const openDialog = () => {
    setIsOpen(true);
  };

  const closeDialog = () => {
    if (isPageAnalyticsEnabled) {
      const eventData: TrackingEvent = {
        eventName: KEEP_EDITING_PRESSED,
      };

      getTrackingAdapter.sendLinkEvent(eventData);
    } else {
      sendLinkEvent(tealium, editKeepButtonClicked);
    }
    setIsOpen(false);
    resetLinkHref();
  };

  const onDiscard = async () => {
    dispatch(actionUpdateIsDiscarding(true));

    if (isPageAnalyticsEnabled) {
      const eventData: TrackingEvent = {
        eventName: DISCARD_CHANGES_PRESSED,
      };

      getTrackingAdapter.sendLinkEvent(eventData);
    } else {
      sendLinkEvent(tealium, editDiscardButtonClicked);
    }

    dispatch(utils(UTILS_RESET_PERSISTENT_DATA));

    setTimeout(() => {
      if (linkHref) {
        // This link is already localised in the Header/Footer
        window.location.href = linkHref;
      } else {
        redirectToJobManagement();
      }
    }, 0);
  };

  const onLinkClick = useCallback(
    (event: MouseEvent) => {
      const href = (event.currentTarget as HTMLAnchorElement).getAttribute(
        'href',
      );

      if (href) {
        if (href.includes('#')) {
          return;
        }

        event.preventDefault();

        if (
          editPageSectionsManager.hasSectionsInEditMode() ||
          editPageSectionsManager.hasSectionsWithSavedChanges() ||
          pathname.current.includes(ROUTE_EDIT_PAY_AND_POST)
        ) {
          openDialog();
          setLinkHref(href);
        } else {
          // This link is already localised in the Header/Footer
          window.location.href = href;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // eslint-disable-next-line react-hooks/exhaustive-deps
      editPageSectionsManager.hasSectionsInEditMode(),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      editPageSectionsManager.hasSectionsWithSavedChanges(),
    ],
  );

  useHeaderFooterLinkEventListener({ onLinkClick });

  return {
    isOpen,
    openDialog,
    closeDialog,
    onDiscard,
  };
};
